import { Link, useNavigate, useLocation } from "@tanstack/react-router";
import { useCity } from "@/context/CityContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { CITIES, type CityId } from "@/data/cities";
import { usePublicCities } from "@/hooks/use-public-cities";
import { useState, useRef, useEffect } from "react";
import { Search, Check, LayoutDashboard, Settings, LogOut, LifeBuoy, CreditCard, Shield, Users, Megaphone, Handshake, Building2 } from "lucide-react";
import { MarkerIcon } from "./icons/MarkerIcon";
import { Logo } from "./Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { HeaderModeSwitch } from "./HeaderModeSwitch";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getMyProfile } from "@/lib/profile.functions";
import { avatarSrc } from "@/lib/avatar-url";
import { getMyWorkspaceAccess } from "@/lib/workspace-access.functions";

export function Header() {
  const { city, setCity } = useCity();
  const { pathname } = useLocation();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user, session, loading, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const fetchProfile = useServerFn(getMyProfile);
  const fetchWorkspaces = useServerFn(getMyWorkspaceAccess);
  const { data: workspaces } = useQuery({
    queryKey: ["workspace-access", user?.id],
    queryFn: async () => {
      try {
        return await fetchWorkspaces();
      } catch {
        return { admin: false, crm: false, smm: false, partner: false, business: false };
      }
    },
    enabled: !loading && !!user && !!session?.access_token,
    staleTime: 60_000,
    retry: false,
  });
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      try {
        return await fetchProfile();
      } catch {
        // Auth race / stale session — header gracefully shows initials.
        return null;
      }
    },
    enabled: !loading && !!user && !!session?.access_token,
    retry: false,
  });
  const initials = (profile?.display_name || user?.email || "?").trim().slice(0, 1).toUpperCase();
  const rawAvatarUrl = avatarSrc(user?.id, profile?.avatar_url ?? null, 96);
  const [avatarBroken, setAvatarBroken] = useState(false);
  useEffect(() => { setAvatarBroken(false); }, [rawAvatarUrl]);
  const avatarUrl = rawAvatarUrl && !avatarBroken ? rawAvatarUrl : null;

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    };
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const allCities = usePublicCities();
  // Business mode is a separate product: it only operates in Helsinki.
  const isBusiness = pathname.startsWith("/business");
  const isFi = pathname === "/fi" || pathname.startsWith("/fi/");
  // Main homepage shows Finland as the location; city pages show their own city.
  const isHome = pathname === "/" || pathname === "/fi" || pathname === "/fi/";

  const cities = isBusiness ? allCities.filter((c) => c.slug === "helsinki") : allCities;

  // A destination page: "/<slug>" or "/fi/<slug>" where the slug is a real city.
  const citySegment = (isFi ? pathname.replace(/^\/fi\/?/, "") : pathname.replace(/^\//, "")).split("/")[0];
  const isCityPage =
    !isBusiness && !isHome && !!citySegment && allCities.some((c) => c.slug === citySegment);


  // Keep the business side pinned to Helsinki even if another city was chosen.
  useEffect(() => {
    if (isBusiness && city !== "helsinki") setCity("helsinki" as CityId);
  }, [isBusiness, city, setCity]);

  const current =
    cities.find((c) => c.slug === city) ??
    (isBusiness
      ? cities[0] ?? { slug: "helsinki", name: "Helsinki" }
      : CITIES.find((c) => c.id === city) ??
        cities[0] ??
        CITIES[0] ?? { slug: city, name: (city as string) || "Helsinki" });

  // Homepage pill reads "Finland" (Suomi in FI); city pages show their city.
  const pillLabel = isHome && !isBusiness ? t("nav.finland") : current.name;

  const navLinks = isBusiness
    ? [
        { to: "/business/cities" as const, label: t("nav.city"), tabletHide: false },
        { to: "/business/events" as const, label: t("nav.events"), tabletHide: false },
      ]
    : [
        { to: "/do" as const, label: t("nav.todo"), tabletHide: false },
        { to: "/happening" as const, label: t("nav.events"), tabletHide: false },
        { to: "/eat" as const, label: t("nav.restaurants"), tabletHide: true },
      ];

  return (
    <header className="relative z-40 pt-1 sm:pt-4 px-3 sm:px-5">
      <div
        className={`max-w-7xl mx-auto rounded-full border transition-[box-shadow,border-color] duration-300 relative gpu-layer ios-no-blur bg-gradient-to-b from-white/70 via-white/55 to-white/40 ring-1 ring-white/60 before:pointer-events-none before:absolute before:inset-x-3 before:top-0 before:h-1/2 before:rounded-full before:bg-gradient-to-b before:from-white/80 before:to-transparent before:opacity-90 before:-z-10 after:pointer-events-none after:absolute after:inset-x-6 after:top-[2px] after:h-px after:bg-gradient-to-r after:from-transparent after:via-white after:to-transparent after:opacity-80 ${
          scrolled
            ? "border-white/70 shadow-[0_14px_50px_-14px_rgba(15,30,60,0.32),inset_0_1px_0_rgba(255,255,255,0.95),inset_0_-1px_0_rgba(255,255,255,0.35)] backdrop-blur-2xl backdrop-saturate-150 supports-[-webkit-touch-callout:none]:bg-white/92"
            : "border-white/60 shadow-[0_10px_36px_-14px_rgba(15,30,60,0.22),inset_0_1px_0_rgba(255,255,255,0.9),inset_0_-1px_0_rgba(255,255,255,0.3)] backdrop-blur-2xl backdrop-saturate-150 supports-[-webkit-touch-callout:none]:bg-white/92"
        }`}
      >

        <div className="relative z-10 h-14 sm:h-16 px-2 sm:px-5 flex items-center justify-between gap-2 sm:gap-4 lg:gap-6">
          <div className="shrink-0">
            <Logo />
          </div>

          <nav className="hidden md:flex items-center gap-0.5 lg:gap-1 text-sm font-medium">
            {navLinks.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                className={`${l.tabletHide ? "hidden lg:inline-flex" : "inline-flex"} px-2.5 lg:px-4 py-2 rounded-full text-navy/70 hover:text-navy hover:bg-white/70 transition-colors text-[13px] lg:text-sm whitespace-nowrap`}
                activeProps={{ className: `${l.tabletHide ? "hidden lg:inline-flex" : "inline-flex"} px-2.5 lg:px-4 py-2 rounded-full text-navy bg-white/80 shadow-sm text-[13px] lg:text-sm whitespace-nowrap` }}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <HeaderModeSwitch />

          <div className="flex items-center gap-1.5 rounded-full bg-white/60 backdrop-blur-xl border border-white/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.6),0_8px_24px_-12px_rgba(15,30,60,0.2)] p-1 pl-1">
            <button
              type="button"
              onClick={() => navigate({ to: "/search", search: { q: "", s: "all" } })}
              aria-label={t("nav.search")}
              className="hidden sm:grid size-9 place-items-center rounded-full text-navy/80 hover:bg-white hover:text-navy hover:shadow-sm transition-all"
            >
              <Search className="size-4" strokeWidth={2.25} />
            </button>

            <span aria-hidden className="hidden sm:inline-block w-px h-5 bg-navy/10" />

            {/* Language — visible on every page */}
            <LanguageSwitcher variant="light" align="right" />

            {/* City — icon-only on mobile, icon + label on larger screens */}
            <div className="relative block" ref={ref}>
              <button
                onClick={() => setOpen((o) => !o)}
                aria-label={`${t("nav.city")}: ${pillLabel}`}
                aria-expanded={open}
                style={{ textTransform: "none" }}
                className="h-9 px-2.5 sm:px-3 inline-flex items-center justify-center gap-1.5 rounded-full text-navy/85 hover:bg-white hover:text-navy hover:shadow-sm transition-all text-xs font-semibold"
              >
                <MarkerIcon className="size-4" />
                <span className="inline">{pillLabel}</span>
              </button>
              {open && (
                <ul
                  className="absolute right-0 mt-3 min-w-[220px] max-h-[70vh] overflow-y-auto bg-white border border-slate-200/80 rounded-2xl shadow-lg shadow-navy/5 overflow-x-hidden p-1.5"
                  role="listbox"
                >
                  {!isBusiness && (
                    <li>
                      <button
                        onClick={() => {
                          setOpen(false);
                          navigate({ to: isFi ? "/fi" : "/" });
                        }}
                        className={`w-full text-left px-3 py-2.5 text-sm rounded-xl transition-colors flex items-center justify-between ${
                          isHome
                            ? "bg-teal/10 text-teal font-semibold"
                            : "text-navy hover:bg-navy/5"
                        }`}
                        role="option"
                        aria-selected={isHome}
                      >
                        {t("nav.finland")}
                        {isHome && <Check className="size-3.5" />}
                      </button>
                    </li>
                  )}
                  <li className="px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.3em] text-navy/45">
                    {isBusiness ? t("nav.chooseCity") : t("nav.chooseDestination")}
                  </li>
                  {cities.map((c) => {
                    const selected = !isHome && c.slug === city;
                    return (
                      <li key={c.slug}>
                        <button
                          onClick={() => {
                            setCity(c.slug as CityId);
                            setOpen(false);
                            // On the homepage and on destination pages a city pick opens that
                            // destination's page; elsewhere (/eat, /do, /place…) just re-filter.
                            if (!isBusiness && (isHome || isCityPage)) {
                              if (isFi) {
                                navigate({ to: "/fi/$citySlug", params: { citySlug: c.slug } });
                              } else {
                                navigate({ to: "/$citySlug", params: { citySlug: c.slug } });
                              }
                            }
                          }}
                          className={`w-full text-left px-3 py-2.5 text-sm rounded-xl transition-colors flex items-center justify-between ${
                            selected
                              ? "bg-teal/10 text-teal font-semibold"
                              : "text-navy hover:bg-navy/5"
                          }`}
                          role="option"
                          aria-selected={selected}
                        >
                          {c.name}
                          {selected && <Check className="size-3.5" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            

            {loading ? (
              <div
                aria-hidden="true"
                className="ml-0.5 size-9 rounded-full bg-navy/10 animate-pulse"
              />
            ) : user ? (
              <div className="relative ml-0.5" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((v) => !v)}
                  aria-label="account menu"
                  aria-expanded={userMenuOpen}
                  className="inline-flex items-center justify-center size-9 rounded-full bg-navy text-white hover:bg-royal hover:shadow-lg hover:shadow-navy/25 transition-all overflow-hidden"
                >
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt=""
                      className="size-9 rounded-full object-cover"
                      width={36}
                      height={36}
                      decoding="async"
                      referrerPolicy="no-referrer"
                      onError={() => setAvatarBroken(true)}
                    />
                  ) : (
                    <span className="text-sm font-semibold">{initials}</span>
                  )}
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-1.5 w-56 rounded-md bg-white border border-slate-200 shadow-xl z-50 overflow-hidden">
                    <Link
                      to="/profile"
                      onClick={() => setUserMenuOpen(false)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      <LayoutDashboard className="size-4" /> Profile
                    </Link>
                    <Link
                      to="/profile"
                      search={{ tab: "membership" }}
                      onClick={() => setUserMenuOpen(false)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      <CreditCard className="size-4" /> Membership
                    </Link>
                    {(workspaces?.admin || workspaces?.crm || workspaces?.smm || workspaces?.partner || workspaces?.business) && (
                      <>
                        <div className="border-t border-slate-100 mt-0.5 pt-0.5" />
                        {workspaces.admin && (
                          <Link
                            to="/admin"
                            search={{ page: "dashboard" }}
                            onClick={() => setUserMenuOpen(false)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          >
                            <Shield className="size-4" /> Admin
                          </Link>
                        )}
                        {workspaces.crm && (
                          <Link
                            to="/crm"
                            onClick={() => setUserMenuOpen(false)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          >
                            <Users className="size-4" /> CRM
                          </Link>
                        )}
                        {workspaces.smm && (
                          <Link
                            to="/crm/smm"
                            onClick={() => setUserMenuOpen(false)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          >
                            <Megaphone className="size-4" /> SMM
                          </Link>
                        )}
                        {workspaces.partner && (
                          <Link
                            to="/partner"
                            onClick={() => setUserMenuOpen(false)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          >
                            <Handshake className="size-4" /> Partner
                          </Link>
                        )}
                        {workspaces.business && (
                          <Link
                            to="/business-admin"
                            search={{ tab: "listings" }}
                            onClick={() => setUserMenuOpen(false)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          >
                            <Building2 className="size-4" /> Business admin
                          </Link>
                        )}
                      </>
                    )}
                    <div className="border-t border-slate-100 mt-0.5 pt-0.5" />
                    <Link
                      to="/profile"
                      onClick={() => setUserMenuOpen(false)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      <Settings className="size-4" /> Settings
                    </Link>
                    <Link
                      to="/help"
                      onClick={() => setUserMenuOpen(false)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      <LifeBuoy className="size-4" /> Support
                    </Link>
                    <button
                      type="button"
                      onClick={async () => {
                        setUserMenuOpen(false);
                        await signOut();
                        navigate({ to: "/" });
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 border-t border-slate-100"
                    >
                      <LogOut className="size-4" /> Log Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="ml-0.5 inline-flex items-center bg-navy text-white px-3 lg:px-5 h-9 rounded-full text-xs lg:text-sm font-semibold whitespace-nowrap hover:bg-royal hover:shadow-lg hover:shadow-navy/25 transition-all"
              >
                {t("nav.signin")}
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
